<template>
    <div class="mc-card" :class="{ 'mc-card--highlight': highlight }">
        <div class="mc-card__head">
            <span class="mc-status" :class="`mc-status--${status.tone}`">
                <v-icon x-small>{{ status.icon }}</v-icon>
                {{ status.label }}
            </span>
            <span class="mc-card__when">{{ formatDate(request.createdAt, 'D/M/YYYY') }}</span>
        </div>

        <div class="mc-card__slots">
            <div v-for="(slot, index) in slots" :key="slot.id" class="mc-slot">
                <router-link :to="{ name: 'Match', params: { id: slot.id } }" class="mc-slot__teams">
                    <span v-for="team in [slot.team1, slot.team2]" :key="team.id" class="mc-slot__team">
                        <span class="mc-slot__logo">
                            <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt="">
                            <v-icon v-else small color="orange">mdi-basketball</v-icon>
                        </span>
                        <span class="mc-slot__name">{{ team.name }}</span>
                    </span>
                </router-link>
                <div class="mc-slot__time">
                    <v-icon x-small color="orange darken-2">mdi-calendar-outline</v-icon>
                    {{ formatDate(slot.matchDatetime) }}<span v-if="slot.arenaName"> · {{ slot.arenaName }}</span>
                </div>
                <div v-if="index === 0" class="mc-card__swap">
                    <v-icon small color="orange darken-2">mdi-swap-vertical</v-icon>
                    <span>ανταλλαγή ημέρας, ώρας και γηπέδου</span>
                </div>
            </div>
        </div>

        <div class="mc-card__meta">
            Αίτημα από <b>{{ request.requesterTeamName }}</b>
            <span v-if="request.reason" class="mc-card__reason">· «{{ request.reason }}»</span>
        </div>

        <div class="mc-votes">
            <div v-for="teamVote in request.votes" :key="teamVote.team.id" class="mc-vote" :class="voteClass(teamVote)">
                <v-icon x-small>{{ voteIcon(teamVote) }}</v-icon>
                <span>{{ teamVote.team.name }}</span>
            </div>
        </div>

        <div v-if="request.status === 'voting' && request.deadline" class="mc-card__deadline">
            Ψηφοφορία έως {{ formatDate(request.deadline) }}
        </div>
        <div v-if="request.status === 'cancelled' && request.cancelledAt" class="mc-card__deadline">
            Ακυρώθηκε στις {{ formatDate(request.cancelledAt) }}
        </div>

        <!-- the captain of a team that has not voted yet -->
        <div v-if="canVote" class="mc-card__actions">
            <v-btn small depressed color="green darken-1" class="white--text" :loading="voting === true" :disabled="voting !== null" @click="vote(true)">
                <v-icon small left>mdi-thumb-up-outline</v-icon>Συμφωνώ
            </v-btn>
            <v-btn small outlined color="red darken-1" :loading="voting === false" :disabled="voting !== null" @click="confirmDecline = true">
                <v-icon small left>mdi-thumb-down-outline</v-icon>Διαφωνώ
            </v-btn>
        </div>
        <!-- the captain of the team that made it, while it is open -->
        <div v-if="canCancel" class="mc-card__actions">
            <v-btn small text color="grey darken-2" :loading="cancelling" @click="confirmCancel = true">
                <v-icon small left>mdi-cancel</v-icon>Ακύρωση αιτήματος
            </v-btn>
        </div>
        <div v-if="error" class="red--text text-body-2 mt-2">{{ error }}</div>

        <v-dialog v-model="confirmCancel" max-width="380">
            <v-card>
                <v-card-title class="font-weight-bold">Ακύρωση του αιτήματος;</v-card-title>
                <v-card-text>Οι αγώνες θα μείνουν ως έχουν και οι αρχηγοί των άλλων ομάδων θα ενημερωθούν. Το αίτημα θα μείνει στο ιστορικό ως ακυρωμένο.</v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text @click="confirmCancel = false">Πίσω</v-btn>
                    <v-spacer />
                    <v-btn depressed color="red darken-1" class="white--text" @click="confirmCancel = false; cancel()">Ακύρωση αιτήματος</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="confirmDecline" max-width="380">
            <v-card>
                <v-card-title class="font-weight-bold">Διαφωνείτε με την αλλαγή;</v-card-title>
                <v-card-text>Το αίτημα θα απορριφθεί και οι αγώνες θα μείνουν ως έχουν.</v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text @click="confirmDecline = false">Άκυρο</v-btn>
                    <v-spacer />
                    <v-btn depressed color="red darken-1" class="white--text" @click="confirmDecline = false; vote(false)">Διαφωνώ</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import matchChangeService from '@/services/matchChange'
    import { STATUS, errorMessage, formatDate, isMyRequest } from '@/utils/matchChange'

    // A match change request: the two matches, the votes of the teams and, for a captain who has not voted yet, the vote
    export default {
        name: 'MatchChangeCard',
        props: {
            request: {
                type: Object,
                required: true
            },
            highlight: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                voting: null,
                confirmDecline: false,
                confirmCancel: false,
                cancelling: false,
                error: null
            }
        },
        computed: {
            status() {
                return STATUS[this.request.status] || { label: this.request.status, icon: 'mdi-information-outline', tone: 'muted' }
            },
            slots() {
                return [this.request.match1, this.request.match2]
            },
            myVote() {
                const team = this.$store.getters.getTeam
                if (!this.$store.getters.getCaptain || !team) return null
                return this.request.votes.find(vote => vote.team.id === team.id) || null
            },
            canCancel() {
                return (this.request.status === 'voting' || this.request.status === 'pending') &&
                    isMyRequest(this.$store, this.request.requesterTeamId)
            },
            canVote() {
                return this.request.status === 'voting' && !!this.myVote && this.myVote.approved === null &&
                    (!this.request.deadline || new Date(this.request.deadline).getTime() > Date.now())
            }
        },
        methods: {
            formatDate,
            voteIcon(vote) {
                if (vote.approved === true) return 'mdi-check-circle'
                if (vote.approved === false) return 'mdi-close-circle'
                return 'mdi-help-circle-outline'
            },
            voteClass(vote) {
                if (vote.approved === true) return 'mc-vote--yes'
                if (vote.approved === false) return 'mc-vote--no'
                return 'mc-vote--open'
            },
            cancel() {
                this.cancelling = true
                this.error = null
                matchChangeService.cancel(this.request.id, {
                    teamId: this.$store.getters.getTeam.id,
                    playerId: this.$store.getters.getPlayer.id
                }).then(resp => {
                    this.$emit('updated', resp.data)
                }).catch(error => {
                    this.error = errorMessage(error, 'Δεν ήταν δυνατή η ακύρωση του αιτήματος.')
                }).finally(() => {
                    this.cancelling = false
                })
            },
            vote(approve) {
                this.voting = approve
                this.error = null
                matchChangeService.vote(this.request.id, {
                    teamId: this.$store.getters.getTeam.id,
                    playerId: this.$store.getters.getPlayer.id,
                    approve
                }).then(resp => {
                    this.$emit('updated', resp.data)
                }).catch(error => {
                    this.error = errorMessage(error, 'Δεν ήταν δυνατή η καταχώρηση της ψήφου.')
                }).finally(() => {
                    this.voting = null
                })
            }
        }
    }
</script>

<style scoped>
.mc-card {
    padding: 16px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    text-align: left;
    transition: box-shadow 0.3s;
}

.mc-card--highlight {
    box-shadow: 0 0 0 2px #ef6c00, 0 4px 14px rgba(239, 108, 0, 0.25);
}

.mc-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.mc-card__when {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.5);
}

.mc-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 800;
}

.mc-status .v-icon {
    color: inherit !important;
}

.mc-status--info {
    background: #e3f2fd;
    color: #1565c0;
}

.mc-status--warn {
    background: #fff3e0;
    color: #e65100;
}

.mc-status--ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.mc-status--bad {
    background: #ffebee;
    color: #c62828;
}

.mc-status--muted {
    background: #eceff1;
    color: #546e7a;
}

.mc-slot__teams {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    color: inherit !important;
    text-decoration: none;
}

.mc-slot__team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-weight: 700;
}

.mc-slot__team + .mc-slot__team::before {
    content: '–';
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.4);
}

.mc-slot__logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
}

.mc-slot__logo img {
    max-width: 100%;
    max-height: 100%;
}

.mc-slot__name {
    word-break: break-word;
}

.mc-slot__time {
    margin-top: 2px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
}

.mc-card__swap {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 0;
    font-size: 0.72rem;
    font-weight: 700;
    color: #ef6c00;
}

.mc-card__meta {
    margin-top: 12px;
    font-size: 0.85rem;
    word-break: break-word;
}

.mc-card__reason {
    color: rgba(0, 0, 0, 0.6);
}

.mc-votes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
}

.mc-vote {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
}

.mc-vote .v-icon {
    color: inherit !important;
}

.mc-vote--yes {
    background: #e8f5e9;
    color: #2e7d32;
}

.mc-vote--no {
    background: #ffebee;
    color: #c62828;
}

.mc-vote--open {
    background: #f5f5f5;
    color: rgba(0, 0, 0, 0.55);
}

.mc-card__deadline {
    margin-top: 8px;
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.6);
}

.mc-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.mc-card__actions .v-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 700;
}
</style>
